import React, {useRef, useState} from 'react';
import {X} from 'lucide-react';
import {translate} from '../../../app/shared/utils/localization-utils';
import {cn} from '../../../lib/utils';
import {clsx} from 'clsx';


interface MultipleEmailSelectProps {
	name: string;
	placeholder: string;
	onChange: (emails: string[]) => void;
	error?: string;
	defaultValue?: string[];
	className?: string;
	inputClassName?: string;
	isRequired?: boolean;
}

export const MultipleTextSelect: React.FC<MultipleEmailSelectProps> = ({
	name,
	placeholder,
	className,
	inputClassName,
	onChange,
	error,
	isRequired = false
}) => {
	const [emails, setEmails] = useState<string[]>([]);
	const [inputValue, setInputValue] = useState('');
	const [invalidEmailError, setInvalidEmailError] = useState<string | null>(null);

	const inputRef = useRef<HTMLInputElement>(null);
	const inputId = `input-${name}`;

	const validateEmail = (email: string) =>
		/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

	const handleAddEmail = () => {
		const email = inputValue.trim();
		if (!email) {
			return;
		}
		if (!validateEmail(email)) {
			setInvalidEmailError('Invalid email');
			return;
		}
		if (emails.includes(email)) {
			setInvalidEmailError('Email already added');
			return;
		}
		const updated = [
			...emails,
			email
		];
		setEmails(updated);
		onChange(updated);
		setInputValue('');
		setInvalidEmailError(null);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			handleAddEmail();
			setInputValue('');

		}
	};

	const removeEmail = (index: number) => {
		const updated = emails.filter((_, i) => i !== index);
		setEmails(updated);
		onChange(updated);
	};

	return (
		<div className={cn('flex flex-col', className)}>
			<div
				className={clsx(
					`relative flex peer w-full bg-white min-h-[56px] outline outline-1 hover:outline-2 focus-within:outline-blue focus-within:outline-2 placeholder:text-transparent text-blue text-sm  rounded-[6px] px-3 py-2 transition duration-300 ease
          				${error ? 'outline-red-600' : ' outline-gray-300'}
           				${emails.length > 0 ? 'items-end justify-between' : 'items-center justify-end'}`,
					inputClassName
				)}
			>
				<div className="flex items-center flex-wrap gap-2 h-full grow">
					{emails.map((email, i) => (
						<div
							key={i}
							className="flex items-center justify-between gap-2 bg-gray-300 px-2 py-1 rounded-full hover:opacity-80 cursor-pointer"
						>
							<span className='text-[16px]'>{email}</span>
							<button type="button" onClick={() => removeEmail(i)}>
								<X size={14} className="text-white bg-grey-dark rounded-full"/>
							</button>
						</div>
					))}
				</div>
				<input id={inputId}
					   ref={inputRef}
					   placeholder=""
					   className={`peer w-full bg-white placeholder:text-transparent text-blue text-sm px-3 py-2 transition duration-300 ease outline-none ${
						   error ? '' : ''
					   }`}
					   onChange={(e) => {
						   setInputValue(e.target.value);
						   if (invalidEmailError) {
							   setInvalidEmailError(null);
						   }
					   }}
					   onKeyDown={handleKeyDown}
					   onBlur={() => {
						   handleAddEmail();
					   }}
					   name={name}
					   type="email"
					   value={inputValue}
				/>
				<label
					htmlFor={inputId}
					className={`
          				  	absolute cursor-text bg-white rounded-l px-1 left-2.5 text-[16px] transition-all transform origin-left
            				peer-placeholder-shown:top-[16px] peer-placeholder-shown:text-[16px] peer-placeholder-shown:scale-90
            				peer-focus:-top-2 peer-focus:left-2.5 peer-focus:text-sm peer-focus:scale-90
            				-top-2 scale-90 ${error ? 'text-error peer-focus:text-error' : 'text-blue peer-focus:text-blue'}
          					${inputValue.length > 0 && 'text-sm'}
          					${emails.length > 0 && '!-top-2 !text-sm'}`}
				>
					{placeholder}
					{isRequired ? <span> *</span> : ''}
				</label>
			</div>
			{invalidEmailError && (
				<p className="mt-[6px] px-[16px] text-[12px] text-error leading-3">
					{translate('formsValidation.email')}
				</p>
			)}
			{error && (
				<p className="mt-[6px] px-[16px] text-[12px] text-error leading-3">
					{error}
				</p>
			)}
		</div>
	);
};
